import CheckIcon from "@material-ui/icons/Check";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Button from "../../../../../components/elements/Button";
import SplitDomainList from "../SplitDomainList";
import classes from "../../PublicationListSelectionSlide.module.scss";
import {
  CustomDomainList,
  Domain,
  DomainListGroup,
  ListType,
  PublicDomainListData,
} from "../../../../../types/campaignTypes";
import { truncateText } from "../../../../../helpers/truncateText";

type CustomSplitDomainListsDashboardProps = {
  customDomainLists: any[];
  selectedDomainListsIds: number[];
  selectedDomainListsGroupIds: string[];
  toggleSelectDomainLists: (payload: PublicDomainListData | PublicDomainListData[], include?: boolean) => void;
  toggleSelectDomainListGroups: (listGroupId: string) => void;
};

const CustomSplitDomainListsDashboard: FC<CustomSplitDomainListsDashboardProps> = ({
  customDomainLists,
  selectedDomainListsIds,
  selectedDomainListsGroupIds,
  toggleSelectDomainLists,
  toggleSelectDomainListGroups,
}) => {
  const columns = [
    { key: "name", label: "Name", minWidth: 350 },
    { key: "actions", label: "Actions", minWidth: 100 },
  ] as const;

  const rows = customDomainLists
    .filter((item) => item.listType === ListType.SPLIT)
    .map((group) => {
      const combinedPublications = group.domainLists.flatMap((dl: any) => dl.domains);

      const lists = group.domainLists.map((dl: any) => ({
        beeswaxListId: dl.beeswaxListId,
        domain: dl?.domains ? dl?.domains[0].domain : `list - ${dl.beeswaxListId}`,
        cpm: 25,
      }));

      return {
        beeswaxListId: null,
        name: group.name,
        domains: combinedPublications,
        createdAt: group.domainLists[0]?.createdAt ?? group.createdAt,
        isSplitGroup: true,
        groupId: group.id,
        lists,
      };
    })
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .map(({ name, domains, groupId, lists }) => {
      const isSelected = selectedDomainListsGroupIds.includes(groupId);

      return {
        id: groupId,
        name: (() => (
          <Tooltip title={name}>
            <Typography>{truncateText(name, 80)}</Typography>
          </Tooltip>
        ))(),
        actions: (() => (
          <div className={classes.actions}>
            <Button
              className={classes.selectButton}
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => {
                toggleSelectDomainListGroups(groupId);
                toggleSelectDomainLists(lists, !isSelected);
              }}
            >
              {isSelected ? "Selected" : "Select"}
              <CheckIcon />
            </Button>
          </div>
        ))(),
        collapsableConfigs: {
          collapsable: true,
          collapsableChild: (
            <SplitDomainList
              isDisabled={isSelected}
              domainsInList={domains}
              toggleSelectDomainLists={toggleSelectDomainLists}
              selectedDomainListsIds={selectedDomainListsIds}
            />
          ),
        },
      };
    });

  return (
    <div className={classes.domainListsDashboardContainer}>
      <div className={classes.header}>
        <Typography variant="h2">Custom Publication Lists</Typography>

        {customDomainLists.length === 0 ? (
          <Typography>These are no custom domain lists yet!</Typography>
        ) : (
          <Typography>These are all of the lists that you've created previously</Typography>
        )}
      </div>

      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default CustomSplitDomainListsDashboard;
